import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { IngestionSignal, IngestionRecord, IngestionSummary } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { ListShell, SortHeader } from "../components/ListShell";
import { useCsvColumns } from "../lib/columnChoice";
import { downloadNarrowed } from "../lib/csvDownload";
import { orAbsent, relativeDate } from "../lib/format";
import { Spinner } from "../components/Spinner";

/* What the engine is finding, as it finds it.
 *
 * James, 2026-09-22: a screen showing the work happening behind the scenes.
 * Every other screen answers a question a rep brought to it. This one answers
 * the question nobody was asking out loud, which is whether the thing is
 * actually doing anything.
 *
 * WHAT IS ON IT AND WHAT IS NOT
 *
 * Only readings that produced records. api.ingestion_signal filters the rest
 * out in the view rather than here, because a list of pages that yielded
 * nothing buries the finds. That a page yielded nothing is still a real fact
 * and the Submit a page screen shows it, where somebody asked about that page.
 *
 * Vendor imports sit beside fetched pages, because they are the same thing to
 * the lake: a source was read and records came out. They are wildly different
 * sizes, so the kind is on every row. One Apollo file produced 42,468 records
 * and a web page usually produces between one and twenty.
 */

const FILTERS = ["source_kind"] as const;

const CSV_DEFAULT = ["source_name", "source_kind", "source_url", "records",
                     "companies", "people", "properties", "projects",
                     "created_at"] as const;

/* The engine's own source kinds, said in words a rep can act on.
 *
 * These are lake.source.kind values, not this screen's invention. A kind this
 * map does not know prints as itself rather than as a blank, because a source
 * kind added to the engine should show up here as an odd word rather than
 * disappear. */
const KIND_WORDS: Record<string, string> = {
  api_vendor: "Vendor file",
  web_page: "Web page",
  feed: "News feed",
  official_record: "Public record",
  manual: "Entered by hand",
};

/* Where a record opens: its own page, /companies/<id>, /people/<id> or
   /properties/<id>, the same path src/components/RecordLink.tsx builds. */
const RECORD_PATH: Record<string, string> = {
  company: "/companies",
  person: "/people",
  property: "/properties",
};

function recordLink(entityType?: string | null, entityId?: string | null) {
  if (!entityType || !entityId) return null;
  const path = RECORD_PATH[entityType];
  if (!path) return null;
  return `${path}/${encodeURIComponent(entityId)}`;
}

/* The records one reading produced, opened under its row.
 *
 * A separate query rather than part of the list, because one vendor import can
 * hold tens of thousands of records and the list has to stay readable. This is
 * capped and says when it is showing part of a set. */
function RecordsFor({ token, signalId, total }:
                    { token: string; signalId: string; total: number }) {
  const CAP = 50;
  const q = useQuery({
    queryKey: ["ingestion-records", signalId],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("ingestion_record")
        .select("signal_id,entity_type,entity_id,label")
        .eq("signal_id", signalId)
        .limit(CAP);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as IngestionRecord[];
    },
  });

  if (q.isLoading) return <p className="muted"><Spinner />Loading.</p>;
  if (q.isError) return <p className="refusal">{(q.error as Error).message}</p>;
  const rows = q.data ?? [];
  if (!rows.length) return <p className="muted">No records to show.</p>;

  return (
    <>
      {total > CAP ? (
        <p className="hint">The first {CAP} of {total.toLocaleString()}.</p>
      ) : null}
      <ul className="records">
        {rows.map((r, i) => {
          const href = recordLink(r.entity_type, r.entity_id);
          const name = (r.label ?? "").trim();
          return (
            <li key={`${r.entity_id}-${i}`}>
              <span className="muted">{r.entity_type}</span>{" "}
              {href
                ? <Link to={href}>{name || "Open the record"}</Link>
                /* A project has no list screen of its own, cut on 2026-09-10,
                   so it is named without a link rather than given a dead one. */
                : (name || orAbsent(null))}
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* The queue, above the list.
 *
 * James, 2026-09-22: how many are queued, how many waiting, how many records in
 * the last seven days.
 *
 * The case it answers. He opened a company's team page, saw many people on it,
 * and the row said two records. Neither natural reading was right: the engine
 * had not chosen two and had not found two new ones. The page was fetched, all
 * 22,501 characters, and was sitting at triaged with nothing extracted yet, in
 * a queue that drains every ten minutes. A record count says nothing until a
 * page has been through extraction, and the list alone gave no way to tell
 * which side of that line a row was on.
 *
 * No time estimate. The timers run every ten minutes and how long a queue takes
 * depends on what is in it, so the depth is the honest number and a predicted
 * finish would be invented.
 */
function Queue({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ["ingestion-summary"],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("ingestion_summary").select("*").limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as IngestionSummary | null;
    },
  });

  if (q.isLoading) return <p className="muted"><Spinner />Loading the queue.</p>;
  // A failed header must not take the list with it. The list is the screen.
  if (q.isError || !q.data) return null;
  const d = q.data;
  const n = (v: number | null | undefined) => (v ?? 0).toLocaleString();

  return (
    <div className="queue">
      <span><strong>{n(d.awaiting_triage)}</strong> waiting to be judged</span>
      <span><strong>{n(d.awaiting_extraction)}</strong> waiting to be read</span>
      {(d.failed ?? 0) > 0
        ? <span className="refusal"><strong>{n(d.failed)}</strong> failed</span>
        : null}
      <span className="muted">Last 7 days: {n(d.arrived_7d)} read,{" "}
        {n(d.resolved_7d)} produced records, {n(d.ignored_7d)} held nothing useful,{" "}
        <strong>{n(d.records_7d)}</strong> records written</span>
      <span className="muted">Last 24 hours: {n(d.records_24h)} records</span>
    </div>
  );
}

export function Ingestion({ session }: { session: SignedIn }) {
  const params = useListParams("created_at", FILTERS, false, "ingestion_signal");
  const csvColumns = useCsvColumns("ingestion", "ingestion_signal", CSV_DEFAULT);
  const queryClient = useQueryClient();
  const [openRow, setOpenRow] = useState<string | null>(null);

  /* One place that says what the list is narrowed to, called by both the rows
     query and the download. Written once rather than twice, because two copies
     of a filter is how a CSV comes to hold rows the screen never showed. */
  const narrowedFor = (columns: string) => {
    let q = apiClient(session.idToken)
      .from("ingestion_signal")
      .select(columns, { count: "exact" });
    if (params.filters["source_kind"]) {
      q = q.eq("source_kind", params.filters["source_kind"]);
    }
    if (params.search.trim()) {
      q = q.ilike("source_name", `*${params.search.trim()}*`);
    }
    return q;
  };

  const LIST_COLUMNS =
    "id,source_name,source_kind,source_tier,source_url,observed_at,created_at," +
    "records,companies,people,properties,projects";

  const query = useQuery({
    queryKey: ["ingestion", params.page, params.sort, params.ascending,
               params.filters, params.search],
    /* The engine writes to this while somebody is looking at it, which is the
       point of the screen, so it refreshes on a timer like Jobs does. Every
       other list does not, because a list must not reorder under a rep
       mid-work. Here the movement is the content. */
    refetchInterval: 30_000,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error, count } = await narrowedFor(LIST_COLUMNS)
        .order(params.sort, { ascending: params.ascending, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return { rows: (data ?? []) as IngestionSignal[], total: count ?? null };
    },
  });

  const rows = query.data?.rows ?? [];

  return (
    <ListShell
      title="Ingestion"
      view="ingestion_signal"
      searchLabel="Source"
      params={params}
      csvColumns={csvColumns}
      total={query.data?.total ?? null}
      rowCount={rows.length}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error as Error | null}
      dataUpdatedAt={query.dataUpdatedAt}
      onRefresh={() => queryClient.invalidateQueries({ queryKey: ["ingestion"] })}
      onRetry={() => query.refetch()}
      onDownload={() =>
        downloadNarrowed({
          name: "ingestion",
          columns: csvColumns.chosen,
          total: query.data?.total ?? null,
          fetchAll: async (ceiling) => {
            const { data, error } = await narrowedFor(csvColumns.chosen.join(","))
              .order(params.sort, { ascending: params.ascending, nullsFirst: false })
              .range(0, ceiling - 1);
            if (error) throw new Error(apiErrorMessage(error));
            return (data ?? []) as unknown as IngestionSignal[];
          },
        })
      }
      emptyEverything="No sources yet. Vendor files and web pages appear here."
      filters={
        <>
          <label htmlFor="f-kind">Source type</label>
          <select id="f-kind" value={params.filters["source_kind"] ?? ""}
                  onChange={(e) => params.set({
                    filters: { ...params.filters, source_kind: e.target.value } })}>
            <option value="">any</option>
            {Object.entries(KIND_WORDS).map(([k, v]) =>
              <option key={k} value={k}>{v}</option>)}
          </select>
        </>
      }
    >
      <Queue token={session.idToken} />
      <table>
        <thead>
          <tr>
            <SortHeader column="source_name" label="Source" params={params} />
            <th>Type</th>
            <SortHeader column="created_at" label="Read" params={params} />
            <SortHeader column="records" label="Records" params={params} numeric />
            <SortHeader column="companies" label="Companies" params={params} numeric />
            <SortHeader column="people" label="People" params={params} numeric />
            <SortHeader column="properties" label="Properties" params={params} numeric />
            <SortHeader column="projects" label="Projects" params={params} numeric />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const open = openRow === s.id;
            return (
              <>
                <tr key={s.id}>
                  <td>
                    <button className="link"
                            aria-expanded={open}
                            onClick={() => setOpenRow(open ? null : (s.id ?? null))}>
                      {orAbsent(s.source_name)}
                    </button>
                    {s.source_url ? (
                      <div className="muted url">{s.source_url}</div>
                    ) : null}
                  </td>
                  <td className="muted">
                    {s.source_kind ? (KIND_WORDS[s.source_kind] ?? s.source_kind)
                                   : orAbsent(s.source_kind)}
                  </td>
                  <td className="muted">{relativeDate(s.created_at)}</td>
                  <td className="num"><strong>{(s.records ?? 0).toLocaleString()}</strong></td>
                  <td className="num">{(s.companies ?? 0).toLocaleString()}</td>
                  <td className="num">{(s.people ?? 0).toLocaleString()}</td>
                  <td className="num">{(s.properties ?? 0).toLocaleString()}</td>
                  <td className="num">{(s.projects ?? 0).toLocaleString()}</td>
                </tr>
                {open && s.id ? (
                  <tr key={`${s.id}-open`}>
                    <td colSpan={8}>
                      <RecordsFor token={session.idToken} signalId={s.id}
                                  total={s.records ?? 0} />
                    </td>
                  </tr>
                ) : null}
              </>
            );
          })}
        </tbody>
      </table>
    </ListShell>
  );
}
